/**
 * BubblesCanvas — animation Canvas 2D de bulles de Champagne montantes.
 *
 * Port React du composant utilisé sur la landing publique (apps/website/src/pages/index.astro).
 * Visuel cohérent entre vitrine et ERP pour identité de marque.
 *
 * Caractéristiques :
 * - Distribution de tailles non uniforme (majorité petites, rares grosses)
 * - Mouvement vertical + wobble horizontal sinusoïdal
 * - Reset en bas quand la bulle sort en haut
 * - DPR-aware (crisp sur écrans Retina/HiDPI)
 * - Pause automatique hors écran (perf via IntersectionObserver)
 * - Respect de `prefers-reduced-motion`
 * - Frame statique sur appareil tactile ou en économie de données
 */

import { useEffect, useRef } from "react";

interface BubblesCanvasProps {
  /** Nombre de bulles affichées. Défaut 90 (densité moyenne, OK mobile). */
  count?: number;
  /** Classes Tailwind/CSS additionnelles (positionnement). */
  className?: string;
  /**
   * Tonalité des bulles selon le fond derrière le canvas :
   * - "light" (défaut) : bulles dorées translucides sur fond crème — cas ERP / pages auth.
   * - "dark" : bulles crème lumineuses sur fond marron foncé — cas hero landing.
   * Le choix est visuel, pas fonctionnel : les deux modes utilisent exactement
   * la même physique (taille, vitesse, wobble), seul le ctx.fillStyle change.
   */
  tone?: "light" | "dark";
}

export function BubblesCanvas({ count = 90, className, tone = "light" }: BubblesCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    // Sur mobile terrain, le canvas couvre tout l'écran et resterait sinon à
    // 60 fps pendant toute la session. Une frame statique conserve l'identité
    // visuelle sans consommer batterie/CPU. Même politique avec reduced-motion
    // et l'économiseur de données du navigateur.
    const reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const coarsePointerQuery = window.matchMedia("(pointer: coarse)");
    const saveData =
      (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData ===
      true;
    let reduce = reduceMotionQuery.matches || coarsePointerQuery.matches || saveData;

    interface Bubble {
      x: number;
      y: number;
      r: number;
      vy: number;
      phase: number;
      wobble: number;
      alpha: number;
      stream: number;
    }

    let bubbles: Bubble[] = [];
    let canvasWidth = 0;
    let canvasHeight = 0;

    const resize = () => {
      // DPR cap à 2 : sur un 4K à DPR 3, x9 pixels à dessiner = saccades garanties.
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvasWidth = parent.clientWidth;
      canvasHeight = parent.clientHeight;
      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;
      canvas.style.width = canvasWidth + "px";
      canvas.style.height = canvasHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const populate = () => {
      bubbles = [];
      for (let i = 0; i < count; i++) {
        // Math.pow(rand, 2.2) biaise la distribution vers les petites valeurs :
        // beaucoup de bulles fines, rares grosses bulles — proche d'une vraie flûte.
        const r = 0.6 + Math.pow(Math.random(), 2.2) * 2.6;
        bubbles.push({
          x: Math.random() * canvasWidth,
          y: Math.random() * canvasHeight,
          r,
          // Plus une bulle est grosse, plus elle monte vite (Stokes inversé).
          vy: 0.4 + r * 0.35 + Math.random() * 0.5,
          phase: Math.random() * Math.PI * 2,
          wobble: 0.15 + Math.random() * 0.35,
          alpha: 0.45 + Math.random() * 0.45,
          // Chaque bulle suit un "stream" (axe vertical), avec un wobble autour.
          stream: Math.random() * canvasWidth,
        });
      }
    };

    resize();
    populate();

    const handleResize = () => {
      resize();
      populate();
    };
    window.addEventListener("resize", handleResize);

    let running = !reduce;
    let raf: number | null = null;

    const draw = (t: number) => {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      for (const b of bubbles) {
        b.y -= b.vy;
        b.x = b.stream + Math.sin(t * 0.0015 + b.phase) * b.wobble * 8;
        // Bulle sortie par le haut → repop en bas, sur un nouveau stream
        if (b.y + b.r < -10) {
          b.y = canvasHeight + 20;
          b.stream = Math.random() * canvasWidth;
        }

        if (tone === "dark") {
          // Bulles crème lumineuses (fond marron foncé du hero landing).
          // Remplissage simple : la bulle est plus claire que le fond → elle ressort.
          ctx.fillStyle = `rgba(255, 240, 200, ${b.alpha})`;
          ctx.beginPath();
          ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
          ctx.fill();
          if (b.r > 1.4) {
            ctx.fillStyle = `rgba(255, 255, 255, ${b.alpha * 0.7})`;
            ctx.beginPath();
            ctx.arc(b.x - b.r * 0.3, b.y - b.r * 0.3, b.r * 0.35, 0, Math.PI * 2);
            ctx.fill();
          }
        } else {
          // Bulles dorées translucides (fond crème champagne-50 du login ERP).
          // Sur fond clair, un remplissage plein noierait l'effet → on combine :
          // 1) un fill or très transparent pour suggérer le volume
          // 2) un stroke or-sombre fin pour redonner du contour
          // 3) un reflet blanc cassé en haut-gauche pour l'effet "bulle de gaz"
          ctx.fillStyle = `rgba(200, 156, 44, ${b.alpha * 0.32})`;
          ctx.beginPath();
          ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = `rgba(125, 95, 28, ${b.alpha * 0.7})`;
          ctx.lineWidth = 0.7;
          ctx.stroke();
          if (b.r > 1.4) {
            ctx.fillStyle = `rgba(255, 252, 235, ${b.alpha * 0.85})`;
            ctx.beginPath();
            ctx.arc(b.x - b.r * 0.3, b.y - b.r * 0.3, b.r * 0.32, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      if (running) raf = requestAnimationFrame(draw);
    };

    if (running) raf = requestAnimationFrame(draw);
    else draw(0);

    // Applique immédiatement un changement de préférence ou de périphérique.
    const handleMotionPolicyChange = () => {
      reduce = reduceMotionQuery.matches || coarsePointerQuery.matches || saveData;
      if (reduce) {
        running = false;
        if (raf) cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(draw);
      }
    };
    reduceMotionQuery.addEventListener("change", handleMotionPolicyChange);
    coarsePointerQuery.addEventListener("change", handleMotionPolicyChange);

    // Pause quand le canvas est hors écran (économie CPU/batterie sur mobile).
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !reduce) {
            if (!running) {
              running = true;
              raf = requestAnimationFrame(draw);
            }
          } else {
            running = false;
            if (raf) cancelAnimationFrame(raf);
          }
        });
      },
      { threshold: 0.05 },
    );
    observer.observe(canvas);

    return () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleResize);
      reduceMotionQuery.removeEventListener("change", handleMotionPolicyChange);
      coarsePointerQuery.removeEventListener("change", handleMotionPolicyChange);
      observer.disconnect();
    };
  }, [count, tone]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className ?? "pointer-events-none absolute inset-0 h-full w-full"}
    />
  );
}
